import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import Button from "../../components/ui/Button";
import { GUIDED_TOUR_CTA_LABEL } from "./constants";
import { useGuidedTour } from "./GuidedTourContext";

/**
 * Compact floating tour controls — does not take a full footer band,
 * so scenes keep most of the viewport for form/PDF preview.
 */
const TourChrome = () => {
  const { step, stepIndex, stepCount, isFirst, isLast, next, prev, goToSignup } =
    useGuidedTour();

  const progress = ((stepIndex + 1) / stepCount) * 100;

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex justify-center p-3 sm:p-4 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="pointer-events-auto w-full max-w-xl overflow-hidden rounded-2xl border border-primary/35 bg-card/95 shadow-elevation-lg backdrop-blur-md ring-2 ring-primary/25"
      >
        <div className="h-1 w-full bg-muted overflow-hidden">
          <motion.div
            className="h-full bg-primary"
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>

        <div className="flex flex-col gap-2.5 px-3 py-2.5 sm:px-4 sm:py-3">
          <AnimatePresence mode="wait">
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.22 }}
              className="min-w-0"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">
                Passo {stepIndex + 1} de {stepCount}
              </p>
              <p className="text-sm font-semibold text-foreground leading-snug truncate">
                {step.title}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground leading-snug line-clamp-2">
                {step.body}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={prev}
              disabled={isFirst}
              className="shrink-0"
            >
              Anterior
            </Button>

            {!isLast ? (
              <motion.div
                className="relative shrink-0"
                animate={{ scale: [1, 1.04, 1] }}
                transition={{
                  repeat: Infinity,
                  duration: 1.8,
                  ease: "easeInOut",
                }}
              >
                <motion.span
                  aria-hidden
                  className="pointer-events-none absolute -inset-1 rounded-xl bg-primary/25"
                  animate={{ opacity: [0.35, 0.75, 0.35], scale: [1, 1.06, 1] }}
                  transition={{
                    repeat: Infinity,
                    duration: 1.8,
                    ease: "easeInOut",
                  }}
                />
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={next}
                  className="relative shadow-soft-md ring-2 ring-primary/50 ring-offset-2 ring-offset-card"
                >
                  Próximo
                </Button>
              </motion.div>
            ) : (
              <motion.div
                className="relative min-w-0 flex-1"
                animate={{ scale: [1, 1.02, 1] }}
                transition={{
                  repeat: Infinity,
                  duration: 2,
                  ease: "easeInOut",
                }}
              >
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  className="w-full ring-2 ring-primary/50 ring-offset-2 ring-offset-card"
                  onClick={goToSignup}
                >
                  <span className="truncate">{GUIDED_TOUR_CTA_LABEL}</span>
                </Button>
              </motion.div>
            )}

            {!isLast ? (
              <button
                type="button"
                onClick={goToSignup}
                className="ml-auto text-xs font-medium text-primary hover:underline underline-offset-2 shrink-0 max-w-[9rem] truncate sm:max-w-none"
              >
                Conhecer mais
              </button>
            ) : null}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TourChrome;
